import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import {
   CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect, CFormCheck, CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CCardFooter, CSpinner
} from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import ToastNotification from "components/globals/ToastNotification";
import UserSelectionModal from "components/UserSelectionModal";
import TextEditor from 'components/forms/TextEditor';
import { triggerAlert } from 'components/globals/AlertNotification';

const ImprovementOpp = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const [formData, setFormData] = useState(null);
   const location = useLocation();
   const [users, setUsers] = useState([]);
   const [toast, setToast] = useState(null);
   const [showModal, setShowModal] = useState(false);
   const [isEditing, setIsEditing] = useState(true);
   const [loading, setLoading] = useState(false);

   const token = localStorage.getItem("access_token");
   const decodedToken = jwtDecode(token);
   const is_director = decodedToken.is_director;

   const fetchImprovementOppDetails = async () => {
      let data = null; // Declare a variable to store API response

      try {
         const response = await apiClient.get(`/improvement-opportunities/${id}/`);
         data = response.data; // Store response data
         setFormData(data); // Update state
      } catch (error) {
         triggerAlert('Oportunidade de Melhoria não foi encontrada!', 'error');
         navigate("/oportunidades-melhoria");
      } finally {
         setIsEditing(data?.action === null); // Use the local variable, NOT formData
      }
   };

   useEffect(() => {
      const fetchUsers = async () => {
         try {
            let response;
            if (formData !== null && formData.action != null && formData.responsible_members.length > 0) {
               response = await apiClient.get('/users/by-ids/', {
                  params: { ids: formData.responsible_members },
               });
            } else {
               response = await apiClient.get('/users/active/');
            }
            setUsers(response.data);
         } catch (error) {
            console.error('Error fetching users:', error);
         }
      };

      fetchImprovementOppDetails();
      fetchUsers();

   }, [id, location.pathname]);

   const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;
      let updatedValue;

      if (type === "checkbox") {
         updatedValue = checked; // true if checked, false if unchecked
      } else if (value === "true") {
         updatedValue = true;
      } else if (value === "false") {
         updatedValue = false;
      } else {
         updatedValue = value || null; // Convert empty values to null
      }

      setFormData((prevFormData) => {
         let updatedForm = {
            ...prevFormData,
            [name]: updatedValue,
         };

         return updatedForm;
      });
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      try {
         await apiClient.put(`/improvement-opportunities/${id}/`, formData);
         triggerAlert('Oportunidade de Melhoria atualizada com sucesso!', 'success');
         navigate(`/oportunidades-melhoria`);
      } catch (error) {
         const errorMessage = error.response?.data?.error || 'Erro ao gravar';
         setToast({ title: 'Oportunidade de Melhoria', message: { errorMessage }, type: 'error' });
         setLoading(false);
      }
   };

   const handleCheckboxUsersChange = (userId, isChecked) => {
      setFormData((prevData) => {
         let updatedMembers = isChecked
            ? [...prevData.responsible_members, userId]
            : prevData.responsible_members.filter((id) => id !== userId);

         return { ...prevData, responsible_members: updatedMembers };
      });
   };

   if (formData !== null) {
      if (!isEditing && (formData.action !== null || !is_director)) {
         const renderText = (text) => {
            return <div dangerouslySetInnerHTML={{ __html: text || "-" }} />;
         };
         return (
            <div className="container mt-4">
               <h4 className="text-center mb-5">Detalhes da Oportunidade de Melhoria</h4>
               {[
                  { label: "Código", value: formData.code },
                  { label: "Origem", value: formData.origin || "-" },
                  {
                     label: "Natureza",
                     value: (
                        <>
                           {formData.organizational && <div>Organizacional</div>}
                           {formData.service && <div>Serviço</div>}
                           {formData.marketing && <div>Marketing</div>}
                           {formData.process && <div>Processo</div>}
                        </>
                     )
                  },
                  { label: "Descrição", value: renderText(formData.description) },
                  {
                     label: "Responsáveis",
                     value: formData.responsible_members.length > 0 ? (
                        <ul>
                           {users
                              .filter((user) => formData.responsible_members.includes(user.id))
                              .map((user) => (
                                 <li key={user.id}>{`${user.first_name} ${user.last_name}`}</li>
                              ))}
                        </ul>
                     ) : (
                        <p className="text-muted">Nenhum responsável selecionado</p>
                     )
                  },
                  {
                     label: "Seguimento",
                     value: formData.action === "true" ? "Sim" : formData.action === "false" ? "Não" : ""
                  },
                  { label: "Plano de Contingência", value: formData.contingency_plan ? "Sim" : "Não" },
                  { label: "Observações", value: renderText(formData.obs) }
               ].map((item, index) => (
                  <CCard key={index} className="mb-3">
                     <CCardHeader>
                        <h5>{item.label}</h5>
                     </CCardHeader>
                     <CCardBody>
                        <div>{item.value || ""}</div>
                     </CCardBody>
                  </CCard>
               ))}

               {/* Back Button */}
               <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
                  <CButton color="danger" className="text-white" onClick={() => navigate('/oportunidades-melhoria')}>
                     Voltar
                  </CButton>
               </div>
            </div>
         );
      }

      return (
         <div className="container mt-4">

            <ToastNotification toast={toast} setToast={setToast} />

            <CCard>
               <CCardHeader>
                  <h3>{id ? 'Editar Oportunidade de Melhoria' : 'Nova Oportunidade de Melhoria'}</h3>
               </CCardHeader>
               <CCardBody>
                  <CForm onSubmit={handleSubmit}>
                     <CFormInput
                        type="text"
                        label={<strong>Código</strong>}
                        name="code"
                        value={formData.code}
                        readOnly
                        plainText
                        className="mb-3"
                     />
                     <CFormInput
                        type="text"
                        label={<strong>Fonte</strong>}
                        value={formData.idea ? "Ideia/Sugestão" : formData.nonconformity ? "Não conformidade" : "Reclamação"}
                        readOnly
                        plainText
                        className="mb-3"
                     />

                     <CFormInput
                        type="text"
                        label={<strong>Origem</strong>}
                        name="origin"
                        value={formData.origin}
                        readOnly
                        plainText
                        className="mb-3"
                     />

                     <div className="mt-3 mb-3">
                        <div><strong>Natureza</strong></div>
                        <CFormCheck
                           label="Organizacional"
                           name="organizational"
                           checked={formData.organizational}
                           onChange={handleInputChange}
                        />
                        <CFormCheck
                           label="Serviço"
                           name="service"
                           checked={formData.service}
                           onChange={handleInputChange}
                        />
                        <CFormCheck
                           label="Marketing"
                           name="marketing"
                           checked={formData.marketing}
                           onChange={handleInputChange}
                        />
                        <CFormCheck
                           label="Processo"
                           name="process"
                           checked={formData.process}
                           onChange={handleInputChange}
                        />
                     </div>

                     <TextEditor
                        label="Descrição"
                        value={formData.description ?? ''}
                        onChange={(value) =>
                           handleInputChange({ target: { name: "description", value } })
                        }
                        className="mb-3"
                     />

                     <CFormSelect
                        label={<strong>Seguimento</strong>}
                        name="action"
                        value={formData.action ?? ''}
                        onChange={handleInputChange}
                        className="mb-3"
                     >
                        <option value="">Selecione</option>
                        <option value="true">Sim</option>
                        <option value="false">Não</option>
                     </CFormSelect>

                     <CButton color="info" className="text-white" onClick={() => setShowModal(true)}>
                        Selecionar Responsáveis
                     </CButton>

                     <UserSelectionModal
                        title="Selecionar Responsáveis"
                        visible={showModal}
                        onClose={() => setShowModal(false)}
                        users={users}
                        selectedUsers={formData.responsible_members}
                        onChange={handleCheckboxUsersChange}
                     />

                     <div className="mt-3 mb-3">
                        <div><strong>Plano de Contigência</strong></div>
                        <CFormCheck
                           name="contingency_plan"
                           checked={formData.contingency_plan}
                           onChange={handleInputChange}
                        />
                     </div>

                     <TextEditor
                        label="Observações"
                        value={formData.obs ?? ''}
                        onChange={(value) =>
                           handleInputChange({ target: { name: "obs", value } })
                        }
                        className="mb-3"
                     />

                     <CModal visible={showModal} onClose={() => setShowModal(false)}>
                        <CModalHeader>
                           <CModalTitle>Selecionar Responsáveis</CModalTitle>
                        </CModalHeader>
                        <CModalBody>
                           {users.map((user) => (
                              <CFormCheck
                                 key={user.id}
                                 label={`${user.first_name} ${user.last_name}`}
                                 checked={formData.responsible_members.includes(user.id)}
                                 onChange={(e) => handleCheckboxUsersChange(user.id, e.target.checked)}
                              />
                           ))}
                        </CModalBody>
                        <CModalFooter>
                           <CButton color="secondary" onClick={() => setShowModal(false)}>
                              Fechar
                           </CButton>
                        </CModalFooter>
                     </CModal>
                  </CForm>
               </CCardBody>
               <CCardFooter className="d-flex justify-content-end gap-2">
                  <CButton color="danger" className="text-white" onClick={() => navigate("/improvement-opportunities")}>
                     Cancelar
                  </CButton>
                  <CButton disabled={loading} color="success" className="text-white" type="submit" onClick={handleSubmit}>
                     {loading ? <CSpinner size="sm" /> : 'Confirmar'}
                  </CButton>
               </CCardFooter>
            </CCard>
         </div>
      );
   }
};

export default ImprovementOpp;